import React from 'react';
import { 
  Home, 
  Key, 
  BarChart3, 
  FileText, 
  Settings, 
  User,
  Users,
  Menu,
  X,
  Video,
  LogOut,
  Shield,
  CreditCard,
  Webhook,
  Monitor,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useState } from 'react';
import { ThemeToggle } from './ThemeToggle';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useAuth } from '../contexts/AuthContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
  onNavigate?: (page: string) => void;
}

const navigation = [
  { name: 'Overview', href: '/', icon: Home },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Webhooks', href: '/webhooks', icon: Webhook },
  { name: 'Video SDK', href: '/video-sdk', icon: Video },
  { name: 'Templates', href: '/templates', icon: Monitor },
  { name: 'Documentation', href: '/docs', icon: FileText },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  children, 
  currentPage = '/',
  onNavigate
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const { account, currentWorkspace, signOut, getCurrentOrganizationId } = useAuth();
  
  // Get current organization for URL construction
  const currentOrgId = getCurrentOrganizationId();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary transition-colors duration-200 lg:flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 
        bg-light-bg-secondary dark:bg-dark-bg-secondary
        border-r border-light-border dark:border-dark-border
        transform transition-all duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarMinimized ? 'lg:w-16' : 'lg:w-64'}
        w-64
      `}>
        <div className="flex h-screen flex-col">
          {/* Logo and close button */}
          <div className={`flex items-center border-b border-light-border dark:border-dark-border flex-shrink-0 ${sidebarMinimized ? 'h-24 flex-col justify-center items-center space-y-4' : 'h-16 justify-between px-6'}`}>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">CC</span>
              </div>
              {!sidebarMinimized && (
                <span className="ml-3 text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Customer Connects
                </span>
              )}
            </div>
            <div className={`flex items-center ${sidebarMinimized ? 'flex-col space-y-2' : 'space-x-2'}`}>
              <button
                className="hidden lg:block text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                onClick={() => setSidebarMinimized(!sidebarMinimized)}
              >
                {sidebarMinimized ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
              </button>
              <button
                className="lg:hidden text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                onClick={() => setSidebarOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Workspace Switcher */}
          {!sidebarMinimized && (
            <div className="p-4 border-b border-light-border dark:border-dark-border flex-shrink-0">
              <WorkspaceSwitcher />
            </div>
          )}

          {/* Navigation - Scrollable if needed */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto min-h-0">
            {navigation.map((item) => {
              const isActive = currentPage === item.href;
              const orgBasedHref = `/${currentOrgId}${item.href}`;
              return (
                <button
                  key={item.name}
                  onClick={() => onNavigate?.(orgBasedHref)}
                  className={`
                    w-full flex items-center text-sm font-medium rounded-lg
                    transition-colors duration-150 ease-in-out
                    ${isActive 
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                      : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                    }
                    ${sidebarMinimized ? 'justify-center py-3' : 'px-3 py-2 text-left'}
                  `}
                  title={sidebarMinimized ? item.name : undefined}
                >
                  <item.icon size={sidebarMinimized ? 24 : 20} className={sidebarMinimized ? '' : 'mr-3'} />
                  {!sidebarMinimized && item.name}
                </button>
              );
            })}
          </nav>

          {/* User section - Fixed at bottom */}
          <div className="border-t border-light-border dark:border-dark-border p-4 flex-shrink-0">
            {!sidebarMinimized ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                      {account?.role === 'admin' ? <Shield size={16} className="text-white" /> : <User size={16} className="text-white" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                        {account ? `${account.firstName} ${account.lastName}` : 'Loading...'}
                      </p>
                      <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                        {account?.email || '—'}
                      </p>
                      {account?.role && (
                        <p className="text-xs text-primary-500 font-medium capitalize">
                          {account.role}
                        </p>
                      )}
                    </div>
                  </div>
                  <ThemeToggle size="sm" />
                </div>
                <button 
                  onClick={handleSignOut}
                  className="flex items-center w-full px-3 py-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
                >
                  <LogOut size={16} className="mr-3" />
                  Sign out
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                  {account?.role === 'admin' ? <Shield size={16} className="text-white" /> : <User size={16} className="text-white" />}
                </div>
                <button 
                  onClick={handleSignOut}
                  className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex flex-col flex-1 w-full min-h-screen lg:min-h-0 transition-all duration-200 ease-in-out ${sidebarMinimized ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Top bar */}
        <header className="flex h-16 items-center justify-between px-6 bg-light-bg-primary dark:bg-dark-bg-primary border-b border-light-border dark:border-dark-border flex-shrink-0">
          <div className="flex items-center space-x-4">
            <button
              className="lg:hidden text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            
            {currentWorkspace && (
              <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <Shield className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {currentWorkspace.organizationName}
                </span>
                <span className="text-xs text-primary-600 dark:text-primary-400">
                  ({currentWorkspace.accessLevel})
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              Customer Connects SaaS Platform
            </span>
            <div className="hidden lg:block">
              <ThemeToggle />
            </div>
          </div>
        </header>

        {/* Page content - Scrollable */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};